/**
 * Admin Portal → Partners → a partner → Contacts: the view logic, kept pure so jest can pin it
 * (partner launch, workstream S5).
 *
 * UC1 (docs/plans/PARTNER_LAUNCH_PLAN_2026_10_17.md §1): a partner gives us people who opted in
 * to hear from Coach Cedar; an admin previews the CSV (rows, rejects by line, duplicates,
 * never-email addresses, addresses that already have an account), commits it with the
 * partner's consent attestation (§3 O4), sends a test to themself, then presses Send (§2 E5:
 * a human button per tag or import). The routes are S1's (contacts) and S2's (send), §7.
 *
 * Privacy (§2 E8, §8): the screen never shows or builds a plain email address, a last name, a
 * user id or a personal token. Rejects come back by CSV line number. The list carries a masked
 * address (`m•••@gmail.com`) and the screen masks it again, in case. Nothing here puts a name
 * or an address into a URL: there is no search by either (a query string lands in access logs).
 *
 * Web-only (the admin SPA).
 */

import { type PartnerStatus } from './partnersLogic';

// ── vocabularies (plan §7; the migration's CHECKs) ───────────────────────────

export type MailState =
  | 'imported' | 'queued' | 'sending' | 'emailed' | 'ambiguous' | 'bounced' | 'complained' | 'unsubscribed' | 'erased';

export const MAIL_STATES: MailState[] = [
  'imported', 'queued', 'sending', 'emailed', 'ambiguous', 'bounced', 'complained', 'unsubscribed', 'erased',
];

export const MAIL_STATE_LABELS: Record<MailState, string> = {
  imported: 'Not emailed yet',
  queued: 'Queued',
  sending: 'Sending',
  emailed: 'Emailed',
  ambiguous: 'Unknown (send not confirmed)',
  bounced: 'Bounced',
  complained: 'Marked as spam',
  unsubscribed: 'Unsubscribed',
  erased: 'Erased',
};

export function mailStateBadge(state: string): 'success' | 'warning' | 'neutral' | 'error' | 'info' {
  switch (state) {
    case 'emailed':
      return 'success';
    case 'queued':
    case 'sending':
      return 'info';
    case 'ambiguous':
    case 'bounced':
      return 'warning';
    case 'complained':
    case 'unsubscribed':
      return 'error';
    default:
      return 'neutral';
  }
}

export type SendVariant = 'link_and_code' | 'link' | 'code';
export const SEND_VARIANTS: SendVariant[] = ['link_and_code', 'link', 'code'];
export const SEND_VARIANT_LABELS: Record<SendVariant, string> = {
  link_and_code: 'Link + code',
  link: 'Link only',
  code: 'Code only',
};
export const SEND_VARIANT_HINTS: Record<SendVariant, string> = {
  link_and_code: 'Their personal page link and their personal code (recommended: the code is the fallback when the link match misses).',
  link: 'Only their personal page link (the page shows their code too).',
  code: 'Only their personal code and the redeem steps — no link.',
};

export type LinkedVia = 'link' | 'code' | 'email_match';
export const LINKED_VIA_LABELS: Record<LinkedVia, string> = {
  link: 'Personal link',
  code: 'Code',
  email_match: 'Email match',
};

/** How an install or signup was tied to the partner (services/partner_attribution.py CAPTURE_METHODS + the launch's paths). */
export const CAPTURE_METHOD_LABELS: Record<string, string> = {
  ios_match: 'iPhone match (network + device)',
  android_referrer: 'Android install referrer',
  ios_clipboard: 'iPhone clipboard hand-off',
  code: 'Code typed in the app',
  deep_link: 'Deep link',
  link: 'Personal page link',
  email_match: 'Email match at signup',
};

/** partner_offer_grants.applied_via (plan §7). */
export const APPLIED_VIA_LABELS: Record<string, string> = {
  typed: 'Typed the code',
  link: 'Personal link (code applied at signup)',
  email_match: 'Email match at signup',
  tag_link: 'Partner page link (shared code applied at signup)',
};

function labelFor(map: Record<string, string>, value: string | null | undefined): string {
  if (!value) return '—';
  return map[value] ?? value.replace(/_/g, ' ');
}

// ── shapes the routes answer (what the screens read; S1 + S2, plan §7) ───────

export interface ContactRow {
  id: string;
  tag_id: string;
  tag: string;
  import_id: string | null;
  first_name: string | null;
  last_initial: string | null;
  email_masked: string | null;
  event_name: string | null;
  event_date: string | null;
  race_name: string | null;
  mail_state: MailState;
  send_variant: SendVariant | null;
  first_emailed_at: string | null;
  last_emailed_at: string | null;
  reminder_sent_at: string | null;
  first_clicked_at: string | null;
  linked_via: LinkedVia | null;
  linked_at: string | null;
  created_at: string;
  /**
   * S1 (#5418): what a Send would answer for this person right now — one of `SEND_VERDICTS` — or
   * null when the contact is not waiting (`mail_state` other than `imported`). Admin only.
   */
  send_verdict?: string | null;
}

export interface ImportPreview {
  tag_id: string;
  /** The file's SHA-256 as the server read it: sent back on commit (`preview_sha256`) — another file is refused (409). */
  file_sha256: string;
  rows: number;
  importable: number;
  duplicates_in_file: number;
  already_on_list: number;
  suppressed: number;
  existing_accounts: number;
  rejects: Array<{ line: number; reason: string }>;
  rejects_total: number;
  extra_columns: string[];
}

export interface ImportResult {
  id: string;
  tag_id: string;
  rows: number;
  /** New contacts stored. */
  imported: number;
  /** Contacts already on this tag's list whose details the file refreshed (S1: re-import updates). */
  updated?: number;
  duplicates_in_file: number;
  already_on_list: number;
  suppressed: number;
  existing_accounts: number;
  rejects_total: number;
  consent_attested_at: string;
}

/**
 * The contacts funnel (S1, services/partner_contacts.CONTACT_FUNNEL_STAGES, #5418), in the order the
 * screen shows it (the lead, 2026-10-21): the list, our email, then everything after it.
 * `delivered` waits for S2's migration 2091, so it shows only when the answer carries it.
 * `joined` comes before `redeemed`: a redeemed code links the account, so every redeemed contact
 * has joined. `qualified` is net of refunds; `reversed` (a bounty a refund has clawed back) sits
 * beside it, outside the chain.
 */
export const CONTACT_FUNNEL_STAGES = [
  'imported', 'emailed', 'delivered', 'clicked', 'store_tapped', 'installed', 'joined', 'redeemed', 'trial', 'paid',
  'qualified', 'reversed',
] as const;
export type ContactFunnelStage = (typeof CONTACT_FUNNEL_STAGES)[number];
export type ContactFunnelCounts = Partial<Record<ContactFunnelStage, number>>;

export const CONTACT_FUNNEL_LABELS: Record<ContactFunnelStage, string> = {
  imported: 'Imported',
  emailed: 'Emailed',
  delivered: 'Delivered',
  clicked: 'Clicked',
  store_tapped: 'Store tapped',
  installed: 'Installed',
  joined: 'Joined',
  redeemed: 'Redeemed',
  trial: 'Trial',
  paid: 'Paid',
  qualified: 'Qualified',
  reversed: 'Reversed',
};

/** "How this is counted" — the server's own `notes` win when it sends them. */
export const CONTACT_FUNNEL_TOOLTIPS: Record<ContactFunnelStage, string> = {
  imported: 'Contacts stored from uploads, the partner API and the webhook (rejected rows, repeats and never-email addresses are not stored).',
  emailed: 'Contacts our mail service accepted an invite for. Bounces and unknown sends are counted separately below.',
  delivered: 'Contacts whose invite the receiving mail server says it delivered.',
  clicked: 'Contacts who opened their personal page at least once.',
  store_tapped: 'Contacts who went on from their personal page to the App Store or Google Play.',
  installed: 'Contacts who opened the app on a phone matched to their personal page visit.',
  joined: 'Contacts linked to a Coach Cedar account (by their link, their code or an email match).',
  redeemed: 'Contacts whose personal code was redeemed — typed in the app, or applied for them at signup.',
  trial: 'Contacts whose account started a free trial.',
  paid: 'Contacts whose account started a paid subscription.',
  qualified: 'Linked accounts that earned the bounty (second monthly charge or first annual charge) and still hold it: net of refunds.',
  reversed: 'Linked accounts whose bounty a refund has clawed back right now.',
};

/**
 * The stage each stage's share is measured against: the nearest earlier stage it is always part
 * of. `clicked` and `joined` are measured against `emailed`: delivery facts start with S2's 2091
 * (older sends have none), and an email match links an account with no click or install. `trial`
 * is measured against `joined`: a trial needs no redeemed code. `reversed` has no share — it sits
 * beside `qualified`.
 */
export const CONTACT_FUNNEL_BASE: Partial<Record<ContactFunnelStage, ContactFunnelStage>> = {
  emailed: 'imported',
  delivered: 'emailed',
  clicked: 'emailed',
  store_tapped: 'clicked',
  installed: 'store_tapped',
  joined: 'emailed',
  redeemed: 'joined',
  trial: 'joined',
  paid: 'trial',
  qualified: 'paid',
};

/** The chain the card shows, in order: `delivered` only when the answer carries it; `reversed` sits beside `qualified`. */
export function contactFunnelDisplay(funnel: ContactFunnelCounts | null | undefined): ContactFunnelStage[] {
  return CONTACT_FUNNEL_STAGES.filter((s) => s !== 'reversed' && (s !== 'delivered' || typeof funnel?.delivered === 'number'));
}

export interface ImportRow {
  id: string;
  tag_id: string;
  tag: string;
  import_via: 'admin_csv' | 'api' | string;
  created_at: string;
  created_by: string | null;
  imported: number;
  /** Contacts already on the list that this import refreshed. */
  updated?: number;
  rows_in?: number;
  rejected_n?: number;
  emailed: number;
  consent_method: string | null;
  consent_source: string | null;
  consent_wording_version: string | null;
  consent_attested_at: string | null;
}

export interface ContactsFunnel {
  funnel: ContactFunnelCounts;
  mail_states: Partial<Record<MailState, number>>;
  ready_to_send: number;
  /** S1 (#5418): the waiting contacts, by what a Send would answer for each (its `sendable` is `ready_to_send`). Admin only. */
  waiting_by_verdict?: Record<string, number>;
  /** S1 (#5418): new people per UTC month, per tag, by route — oldest first. */
  leads_by_month?: LeadsMonthRow[];
  imports: ImportRow[];
  notes?: Record<string, string>;
}

// ── why a contact is waiting (S1 #5418; ADMIN ONLY — a verdict says whether an address holds an account) ──

/**
 * What a Send would answer for a waiting contact right now: services/partner_contact_mailer
 * `VERDICTS`, composed exactly as the Send composes them (gate-pinned). Never shown to a partner.
 * In the order the waiting line reads them.
 */
export const SEND_VERDICTS = [
  'sendable', 'existing_account', 'already_invited', 'suppressed', 'linked', 'no_email', 'code_unusable', 'not_ready',
  'partner_inactive',
] as const;
export type SendVerdict = (typeof SEND_VERDICTS)[number];

/** One person's verdict, in the admin's words (the contacts list and the review queue). */
export const SEND_VERDICT_LABELS: Record<SendVerdict, string> = {
  sendable: 'Ready to send',
  existing_account: 'Already has an account',
  already_invited: 'Already invited from another list',
  suppressed: 'Suppressed: never email',
  linked: 'Already joined',
  no_email: 'No email address',
  code_unusable: 'Their code can’t be used',
  not_ready: 'Their code isn’t made yet',
  partner_inactive: 'The partner or tag is paused',
};

/** The waiting line's words after a count: [one, more than one]. */
const WAITING_WORDS: Record<SendVerdict, [string, string]> = {
  sendable: ['ready to send', 'ready to send'],
  existing_account: ['already has an account', 'already have an account'],
  already_invited: ['already invited', 'already invited'],
  suppressed: ['suppressed', 'suppressed'],
  linked: ['already joined', 'already joined'],
  no_email: ['with no email address', 'with no email address'],
  code_unusable: ['whose code can’t be used', 'whose code can’t be used'],
  not_ready: ['whose code isn’t made yet', 'whose code isn’t made yet'],
  partner_inactive: ['held while the partner or tag is paused', 'held while the partner or tag is paused'],
};

/** "Waiting: 12 ready to send, 3 already have an account, …" — the non-zero verdicts only; null when nobody waits. */
export function waitingByVerdictText(w: Record<string, number> | null | undefined): string | null {
  if (!w) return null;
  const known = SEND_VERDICTS as readonly string[];
  const order = [...SEND_VERDICTS, ...Object.keys(w).filter((k) => !known.includes(k)).sort()];
  const parts = order
    .filter((v) => Number(w[v] || 0) > 0)
    .map((v) => {
      const n = Number(w[v]);
      const words = (WAITING_WORDS as Record<string, [string, string]>)[v];
      return `${n.toLocaleString('en-CA')} ${words ? words[n === 1 ? 0 : 1] : v.replace(/_/g, ' ')}`;
    });
  return parts.length ? `Waiting: ${parts.join(', ')}` : null;
}

/** One person's verdict in the admin's words; a verdict new to this screen in plain words. */
export function sendVerdictLabel(v: string): string {
  return (SEND_VERDICT_LABELS as Record<string, string>)[v] ?? v.replace(/_/g, ' ');
}

/**
 * Why one person's Send is held back on the screen: the verdict's words when it refuses the send.
 * Null when it is sendable, missing (an older server, or a contact not waiting) or new to this
 * screen — then the server decides, and its answer says what happened.
 */
export function sendBlockedByVerdict(v: string | null | undefined): string | null {
  if (!v || v === 'sendable') return null;
  return (SEND_VERDICT_LABELS as Record<string, string>)[v] ?? null;
}

// ── new leads per month (S1 #5418) ───────────────────────────────────────────

/** One row of `leads_by_month`: the people each import ADDED, per UTC month and tag, by route. */
export interface LeadsMonthRow {
  month: string;
  tag_id?: string;
  tag: string;
  csv: number;
  api: number;
  webhook: number;
  total: number;
}

/** services/partner_contacts.LEADS_VIA_COLUMNS (gate-pinned): a column per import route. */
export const LEADS_COLUMNS = ['csv', 'api', 'webhook'] as const;
export const LEADS_COLUMN_LABELS: Record<(typeof LEADS_COLUMNS)[number], string> = { csv: 'CSV', api: 'API', webhook: 'Webhook' };

export type LeadsMonthTotals = { month: string; csv: number; api: number; webhook: number; total: number };

/** One row per month — the tags of a month summed — in the server's order (oldest first). */
export function leadsByMonth(rows: LeadsMonthRow[] | null | undefined): LeadsMonthTotals[] {
  const out: LeadsMonthTotals[] = [];
  const byMonth = new Map<string, LeadsMonthTotals>();
  for (const r of rows || []) {
    let m = byMonth.get(r.month);
    if (!m) {
      m = { month: r.month, csv: 0, api: 0, webhook: 0, total: 0 };
      byMonth.set(r.month, m);
      out.push(m);
    }
    m.csv += Number(r.csv || 0);
    m.api += Number(r.api || 0);
    m.webhook += Number(r.webhook || 0);
    m.total += Number(r.total || 0);
  }
  return out;
}

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "2026-10" → "Oct 2026", read as text so no time zone can move the month. */
export function monthLabel(ym: string): string {
  const m = /^(\d{4})-(\d{2})$/.exec(String(ym ?? ''));
  const i = m ? Number(m[2]) - 1 : -1;
  return m && i >= 0 && i < 12 ? `${MONTH_NAMES[i]} ${m[1]}` : String(ym ?? '');
}

/** How an import came in (services/partner_contacts.IMPORT_VIA). */
export const IMPORT_VIA_LABELS: Record<string, string> = { admin_csv: 'CSV upload', api: 'Partner API', webhook: 'Webhook' };

export function importViaLabel(via: string | null | undefined): string {
  const v = String(via ?? '');
  return IMPORT_VIA_LABELS[v] ?? (v.replace(/_/g, ' ') || '—');
}

export type SendResult = { test?: boolean; sent_to?: string; queued?: number } & Record<string, unknown>;

/** S1's user detail (`GET /api/admin/users/{id}/detail` → `partner`): null when the account has no partner source. */
export interface UserPartnerInfo {
  attributed: boolean;
  partner_id: string | null;
  partner_display_name: string | null;
  partner_code: string | null;
  tag: string | null;
  capture_method: string | null;
  attributed_at: string | null;
  not_attributed_reason: string | null;
  /** The bind's refusal as a stable code (S1); the screen shows the reason's words. */
  not_attributed_code?: string | null;
  offer_applied_via: string | null;
  contact: {
    linked_via: LinkedVia | null;
    linked_at: string | null;
    identity_confirmed: boolean | null;
    event_name: string | null;
    event_date: string | null;
    race_name: string | null;
  } | null;
}

// ── display helpers ──────────────────────────────────────────────────────────

/**
 * `maya.runner@gmail.com` → `m•••@gmail.com`. Idempotent (a masked value stays as it is), so
 * the screen can run whatever the server sent through it: if a plain address ever arrived, the
 * page would still show it masked.
 */
export function maskEmail(value: string | null | undefined): string {
  const v = (value ?? '').trim();
  if (!v) return '—';
  const at = v.lastIndexOf('@');
  const local = at >= 0 ? v.slice(0, at) : v;
  const first = Array.from(local)[0] ?? '';
  const masked = `${first}•••`;
  return at >= 0 ? `${masked}@${v.slice(at + 1)}` : masked;
}

export function contactName(row: Pick<ContactRow, 'first_name' | 'last_initial'>): string {
  const first = (row.first_name ?? '').trim();
  if (!first) return '—';
  const initial = (row.last_initial ?? '').trim();
  return initial ? `${first} ${initial.slice(0, 1).toUpperCase()}.` : first;
}

/** "Race Weekend · 10K · 2026-05-23" — the facts the partner gave, only those set. */
export function contactEventText(row: { event_name?: string | null; race_name?: string | null; event_date?: string | null }): string {
  const parts = [row.event_name, row.race_name, row.event_date ? String(row.event_date).slice(0, 10) : null]
    .map((p) => (p ?? '').trim())
    .filter(Boolean);
  return parts.length ? parts.join(' · ') : '—';
}

export function canResend(row: Pick<ContactRow, 'mail_state'>): boolean {
  return row.mail_state === 'bounced' || row.mail_state === 'ambiguous';
}

export function canErase(row: Pick<ContactRow, 'mail_state'>): boolean {
  return row.mail_state !== 'erased';
}

export interface StageShare {
  stage: ContactFunnelStage;
  base: ContactFunnelStage;
  pct: number | null;
}

/**
 * Each stage's share of its base (`CONTACT_FUNNEL_BASE`), one decimal. Null when the base is 0, or
 * when the stage outnumbers its base: the two are not nested for these contacts, and a share over
 * 100% would mislead. `delivered` only when the answer carries it.
 */
export function contactStageConversions(funnel: ContactFunnelCounts): Partial<Record<ContactFunnelStage, StageShare>> {
  const out: Partial<Record<ContactFunnelStage, StageShare>> = {};
  for (const stage of CONTACT_FUNNEL_STAGES) {
    const base = CONTACT_FUNNEL_BASE[stage];
    if (!base || (stage === 'delivered' && typeof funnel.delivered !== 'number')) continue;
    const a = Number(funnel[base] || 0);
    const b = Number(funnel[stage] || 0);
    out[stage] = { stage, base, pct: a > 0 && b <= a ? Math.round((b / a) * 1000) / 10 : null };
  }
  return out;
}

// ── the CSV file ─────────────────────────────────────────────────────────────

/** services/partner_contacts.CSV_MAX_BYTES (S1; gate-pinned): a larger file is refused (413). */
export const CONTACTS_CSV_MAX_BYTES = 5 * 1024 * 1024;

export function checkContactsFile(f: { name: string; size: number; type: string } | null | undefined): string | null {
  if (!f) return 'Choose the CSV file first.';
  const isCsv = /\.csv$/i.test(f.name || '') || f.type === 'text/csv';
  if (!isCsv) return 'The list must be a .csv file (export it from the spreadsheet as CSV).';
  if (!f.size) return 'That file is empty.';
  if (f.size > CONTACTS_CSV_MAX_BYTES) return `The file is over ${CONTACTS_CSV_MAX_BYTES / (1024 * 1024)} MB — split it into smaller files.`;
  return null;
}

/** The preview in counts only — never an address or a name. */
export function previewLines(p: ImportPreview): Array<{ label: string; value: string; tone?: 'warn' }> {
  const n = (v: number | null | undefined) => Number(v || 0).toLocaleString('en-CA');
  const lines: Array<{ label: string; value: string; tone?: 'warn' }> = [
    { label: 'Rows read', value: n(p.rows) },
    { label: 'Will be imported', value: n(p.importable) },
    { label: 'Rejected', value: n(p.rejects_total), tone: p.rejects_total ? 'warn' : undefined },
    { label: 'Repeated in this file', value: n(p.duplicates_in_file) },
    { label: 'Already on this tag’s list (their details are refreshed)', value: n(p.already_on_list) },
    { label: 'Never email (unsubscribed or suppressed)', value: n(p.suppressed) },
    { label: 'Already have a Coach Cedar account', value: n(p.existing_accounts) },
  ];
  if (p.extra_columns?.length) lines.push({ label: 'Kept as extra columns', value: p.extra_columns.join(', ') });
  return lines;
}

/** What the commit button says, from the preview: new contacts, refreshed ones, or nothing to do. */
export function commitLabel(p: Pick<ImportPreview, 'importable' | 'already_on_list'>): string {
  const n = (v: number) => Number(v || 0).toLocaleString('en-CA');
  const fresh = Number(p.importable || 0);
  const known = Number(p.already_on_list || 0);
  if (fresh && known) return `Import ${n(fresh)} new contact${fresh === 1 ? '' : 's'} · update ${n(known)}`;
  if (fresh) return `Import ${n(fresh)} new contact${fresh === 1 ? '' : 's'}`;
  if (known) return `Update ${n(known)} contact${known === 1 ? '' : 's'} already on the list`;
  return 'Nothing to import';
}

/** The commit's answer in the admin's words. */
export function importResultText(res: Pick<ImportResult, 'imported' | 'updated'>, tag: string): string {
  const n = (v: number) => Number(v || 0).toLocaleString('en-CA');
  const fresh = Number(res.imported || 0);
  const known = Number(res.updated || 0);
  const head = `Imported ${n(fresh)} new contact${fresh === 1 ? '' : 's'} to ${tag}`;
  return `${head}${known ? ` and refreshed ${n(known)} already on the list` : ''}. Next: send yourself a test, then Send.`;
}

/** "Line 5: No email address" — and a closing line when the server listed fewer than it refused. */
export function previewRejectLines(p: ImportPreview): string[] {
  const listed = (p.rejects || []).map((r) => `Line ${r.line}: ${r.reason}`);
  const more = Number(p.rejects_total || 0) - listed.length;
  if (more > 0) listed.push(`…and ${more.toLocaleString('en-CA')} more rejected row${more === 1 ? '' : 's'}.`);
  return listed;
}

// ── consent (plan §3 O4) ─────────────────────────────────────────────────────

/** partner_contact_imports' CHECKs (S1, migration 2066). */
export const CONSENT_METHOD_MAX = 200;
export const CONSENT_SOURCE_MAX = 500;
export const CONSENT_VERSION_MAX = 200;
export const CONSENT_WORDING_MAX = 4000;

export interface ConsentForm {
  /** How they agreed, e.g. "Checkbox on the race registration form". */
  method: string;
  /** Where, e.g. "Race Weekend 2026 registration". */
  source: string;
  /** Which consent text they saw, e.g. "2026-10 v1". */
  wordingVersion: string;
  /** Optional: the exact text they agreed to (stored with the import). */
  wording: string;
  /** The partner confirmed every person on the list agreed. */
  attested: boolean;
}

export function emptyConsentForm(): ConsentForm {
  return { method: '', source: '', wordingVersion: '', wording: '', attested: false };
}

/** The attestation, in the partner's name. Always "Coach Cedar". */
export function consentCheckboxLabel(partnerDisplayName: string): string {
  return `${partnerDisplayName} confirmed every person on this list agreed to receive email from Coach Cedar`;
}

// eslint-disable-next-line no-control-regex
const CONTROL_CHARS = /[\u0000-\u001f\u007f-\u009f]/;
/** The wording may run over several lines; any other control character is refused. */
// eslint-disable-next-line no-control-regex
const CONTROL_CHARS_BUT_LINES = /[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f-\u009f]/;

export function consentErrors(form: ConsentForm): string[] {
  const errors: string[] = [];
  const check = (value: string, field: string, max: number) => {
    const t = (value ?? '').trim();
    if (!t) errors.push(`${field}: required.`);
    else if (t.length > max) errors.push(`${field}: at most ${max} characters.`);
    else if (CONTROL_CHARS.test(t)) errors.push(`${field}: no control characters.`);
  };
  check(form.method, 'How they agreed', CONSENT_METHOD_MAX);
  check(form.source, 'Where they agreed', CONSENT_SOURCE_MAX);
  check(form.wordingVersion, 'Consent wording version', CONSENT_VERSION_MAX);
  const wording = (form.wording ?? '').trim();
  if (wording.length > CONSENT_WORDING_MAX) errors.push(`Consent wording: at most ${CONSENT_WORDING_MAX.toLocaleString('en-CA')} characters.`);
  else if (CONTROL_CHARS_BUT_LINES.test(wording)) errors.push('Consent wording: no control characters.');
  if (!form.attested) errors.push('Tick the confirmation that every person agreed to email from Coach Cedar.');
  return errors;
}

/**
 * The multipart body for the preview (file + tag) and for the commit (the same file + tag + the
 * consent facts + the attestation + the preview's file hash, so a different file is refused).
 * Refuses to build a commit that is not attested.
 */
export function importFormData(
  file: { blob: Blob; name: string },
  tagId: string,
  commit?: { consent: ConsentForm; previewSha256: string },
): FormData {
  const fd = new FormData();
  fd.append('file', file.blob, file.name);
  fd.append('tag_id', tagId);
  if (commit) {
    const { consent, previewSha256 } = commit;
    if (!consent.attested) throw new Error('The partner must attest consent before the list is imported.');
    fd.append('consent_method', consent.method.trim());
    fd.append('consent_source', consent.source.trim());
    fd.append('consent_wording_version', consent.wordingVersion.trim());
    const wording = (consent.wording ?? '').trim();
    if (wording) fd.append('consent_wording', wording);
    fd.append('consent_attested', 'true');
    fd.append('preview_sha256', previewSha256);
  }
  return fd;
}

// ── the list and the funnel's scope ──────────────────────────────────────────

export const CONTACTS_PAGE_SIZE = 50;

export interface ContactScope {
  tagId?: string;
  importId?: string;
}

export interface ContactFilters extends ContactScope {
  mailState?: MailState | '';
  linked?: '' | 'yes' | 'no';
  limit?: number;
  offset?: number;
}

export function contactsQuery(f: ContactFilters): string {
  const qs = new URLSearchParams();
  if (f.tagId) qs.set('tag_id', f.tagId);
  if (f.importId) qs.set('import_id', f.importId);
  if (f.mailState) qs.set('mail_state', f.mailState);
  if (f.linked === 'yes') qs.set('linked', 'true');
  if (f.linked === 'no') qs.set('linked', 'false');
  qs.set('limit', String(f.limit ?? CONTACTS_PAGE_SIZE));
  qs.set('offset', String(f.offset ?? 0));
  return `?${qs.toString()}`;
}

export function funnelScopeQuery(scope: ContactScope): string {
  const qs = new URLSearchParams();
  if (scope.tagId) qs.set('tag_id', scope.tagId);
  if (scope.importId) qs.set('import_id', scope.importId);
  const s = qs.toString();
  return s ? `?${s}` : '';
}

// ── send (plan §2 E5) ────────────────────────────────────────────────────────

/** POST …/contacts/send: one tag (and an import when chosen), one variant; `test_to` for a test. */
export function sendBody(scope: { tagId: string; importId?: string }, variant: SendVariant, testTo?: string): Record<string, unknown> {
  const body: Record<string, unknown> = { tag_id: scope.tagId };
  if (scope.importId) body.import_id = scope.importId;
  body.variant = variant;
  if (testTo) body.test_to = testTo;
  return body;
}

/** A test send unlocks Send for the SAME tag, import and variant only. */
export function testSendKey(scope: ContactScope, variant: SendVariant): string {
  return `${scope.tagId ?? ''}|${scope.importId ?? ''}|${variant}`;
}

/** Why Send is not offered: a paused or ended tag or partner attributes nobody. */
export function sendBlockedReason(tagStatus: PartnerStatus, partnerStatus: PartnerStatus): string | null {
  if (partnerStatus === 'ended') return 'This partner has ended: nothing more is sent for it.';
  if (tagStatus === 'ended') return 'This tag has ended: nothing more is sent for it.';
  if (partnerStatus === 'paused') return 'This partner is paused: resume it before sending — while paused, nobody the email brings in is attributed.';
  if (tagStatus === 'paused') return 'This tag is paused: resume it before sending — while paused, nobody the email brings in is attributed.';
  return null;
}

/** The send's answer in the admin's words: a test, or the counts the server returned. */
export function sendResultText(res: SendResult): string {
  if (res.test) return `Test sent to ${res.sent_to ?? 'your address'} — check that inbox before sending to the list.`;
  const queued = Number(res.queued ?? 0);
  const head = `Queued ${queued.toLocaleString('en-CA')} email${queued === 1 ? '' : 's'}.`;
  const extras = Object.entries(res)
    .filter(([k, v]) => k !== 'queued' && k !== 'test' && k !== 'sent_to' && typeof v === 'number')
    .map(([k, v]) => `${k.replace(/_/g, ' ')} ${Number(v).toLocaleString('en-CA')}`);
  return extras.length ? `${head} Also: ${extras.join(', ')}.` : head;
}

/** A sentence ends once: a name like "Maya R." already carries its period. */
function sentence(s: string): string {
  return /[.!?]$/.test(s) ? s : `${s}.`;
}

/** Why a send skipped someone: S2's flat `skipped_<reason>` counts, else its nested `skipped` object. */
function skipReasons(r: Record<string, unknown>): string[] {
  const flat = Object.entries(r)
    .filter(([k, v]) => k.startsWith('skipped_') && typeof v === 'number' && v > 0)
    .map(([k]) => k.slice('skipped_'.length).replace(/_/g, ' '));
  if (flat.length) return flat;
  const nested = r.skipped && typeof r.skipped === 'object' ? (r.skipped as Record<string, unknown>) : {};
  return Object.entries(nested)
    .filter(([, v]) => typeof v === 'number' && v > 0)
    .map(([k]) => k.replace(/_/g, ' '));
}

/**
 * One person's Send in the review queue (S2's `contact_ids` on `POST …/contacts/send`). It says "Queued" only
 * when the answer shows it: `queued` ≥ 1 (or `true`), or the contact's new mail state. `queued: 0`
 * is a refusal, said with the reasons the server gave (another partner already invited them, the
 * address is suppressed, …). An answer that shows neither claims nothing — the queue's reload shows
 * whether the person left it. `sent` is true, false, or null for "can't tell".
 */
export function sendOneResult(res: unknown, who: string): { sent: boolean | null; text: string } {
  const r: Record<string, unknown> = res && typeof res === 'object' ? (res as Record<string, unknown>) : {};
  const queuedText = sentence(`Queued the invitation for ${who}`);
  const refused = (why: string[]) => ({
    sent: false,
    text: why.length ? `Nothing was sent to ${who} — ${why.join(', ')}.` : sentence(`Nothing was sent to ${who}`),
  });
  const q = r.queued;
  if (typeof q === 'boolean') return q ? { sent: true, text: queuedText } : refused(skipReasons(r));
  if (typeof q === 'number' || (typeof q === 'string' && q.trim() !== '' && Number.isFinite(Number(q)))) {
    return Number(q) >= 1 ? { sent: true, text: queuedText } : refused(skipReasons(r));
  }
  const contact = r.contact && typeof r.contact === 'object' ? (r.contact as Record<string, unknown>) : null;
  const state = typeof r.mail_state === 'string' ? r.mail_state
    : typeof contact?.mail_state === 'string' ? contact.mail_state : null;
  if (state) {
    if (state === 'queued' || state === 'sending' || state === 'emailed') return { sent: true, text: queuedText };
    const label = MAIL_STATE_LABELS[state as MailState];
    return refused([...skipReasons(r), state === 'imported' ? 'they are still waiting' : `status: ${label ?? state}`]);
  }
  return { sent: null, text: `${sentence(`Send requested for ${who}`)} They leave this list once their invitation is queued.` };
}

// ── the user detail Partner section ──────────────────────────────────────────

/** [label, value] rows for the user detail modal's Partner section (dates as YYYY-MM-DD). */
export function userPartnerRows(p: UserPartnerInfo): Array<[string, string]> {
  const day = (v: string | null | undefined) => (v ? String(v).slice(0, 10) : '—');
  const who = p.partner_display_name
    ? `${p.partner_display_name}${p.partner_code ? ` (${p.partner_code})` : ''}`
    : (p.partner_code ?? '—');
  const rows: Array<[string, string]> = [
    ['Partner', who],
    ['Tag', p.tag ?? '—'],
    ['Attributed', p.attributed
      ? `Yes${p.attributed_at ? ` — ${day(p.attributed_at)}` : ''}`
      : `No${p.not_attributed_reason ? ` — ${p.not_attributed_reason}` : ''}`],
  ];
  if (p.capture_method) rows.push(['Captured by', labelFor(CAPTURE_METHOD_LABELS, p.capture_method)]);
  if (p.offer_applied_via) rows.push(['Offer applied by', labelFor(APPLIED_VIA_LABELS, p.offer_applied_via)]);
  if (p.contact) {
    const c = p.contact;
    rows.push(['Linked contact', `via ${labelFor(LINKED_VIA_LABELS, c.linked_via)}${c.linked_at ? ` — ${day(c.linked_at)}` : ''}`]);
    rows.push(['Event', contactEventText(c)]);
    if (c.identity_confirmed != null) rows.push(['Identity confirmed', c.identity_confirmed ? 'Yes' : 'Not yet']);
  }
  return rows;
}
